import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import './App.css';

interface CallbackLog {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  query?: string;
  headers?: string;
  body?: string;
  ip?: string;
  user_agent?: string;
}

function App() {
  const [callbacks, setCallbacks] = useState<CallbackLog[]>([]);
  const [selectedCallback, setSelectedCallback] = useState<CallbackLog | null>(null);
  const [filterMethod, setFilterMethod] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Backend API URL
  const API_URL = process.env.REACT_APP_API_URL ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:3000'
      : 'https://callback.dotgae.com');

  useEffect(() => {
    // Fetch initial callbacks
    fetchCallbacks();

    // Poll for new callbacks every 600 seconds
    const interval = setInterval(() => {
      fetchCallbacks();
    }, 600000);

    return () => clearInterval(interval);
  }, []);

  const fetchCallbacks = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/callbacks`);
      setCallbacks(response.data);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetCallbacks = async () => {
    if (!window.confirm('모든 callback 데이터를 초기화하시겠습니까?')) {
      return;
    }

    setIsLoading(true);
    try {
      await axios.delete(`${API_URL}/api/callbacks`);
      setCallbacks([]);
      setSelectedCallback(null);
      alert('모든 callback이 초기화되었습니다.');
    } catch (error) {
      console.error('Error resetting callbacks:', error);
      alert('초기화 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const parseJSON = (jsonString?: string) => {
    if (!jsonString) return {};
    try {
      return JSON.parse(jsonString);
    } catch {
      return jsonString;
    }
  };

  const filteredCallbacks = callbacks.filter(callback => {
    const methodMatch = filterMethod === 'ALL' || callback.method === filterMethod;
    const searchMatch = searchTerm === '' ||
      callback.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
      callback.ip?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      callback.method.toLowerCase().includes(searchTerm.toLowerCase());
    return methodMatch && searchMatch;
  });

  const getMethodColor = (method: string) => {
    const colors: { [key: string]: string } = {
      GET: '#61affe',
      POST: '#49cc90',
      PUT: '#fca130',
      DELETE: '#f93e3e',
      PATCH: '#50e3c2'
    };
    return colors[method] || '#999';
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🔔 Callback Monitor</h1>
        <p>API callback tracking & inspection</p>
      </header>

      <div className="container">
        <div className="sidebar">
          <div className="filters">
            <div className="filter-group">
              <label>Method Filter:</label>
              <select value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
                <option value="ALL">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search path, IP, method..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="stats">
              <p>Total: <strong>{filteredCallbacks.length}</strong></p>
              <div className="button-group">
                <button
                  onClick={fetchCallbacks}
                  disabled={isLoading}
                  className="refresh-button"
                >
                  {isLoading ? '⟳ Loading...' : '⟳ Refresh'}
                </button>
                <button
                  onClick={resetCallbacks}
                  disabled={isLoading}
                  className="reset-button"
                >
                  🗑️ Reset
                </button>
              </div>
            </div>
          </div>

          <div className="callbacks-list">
            {filteredCallbacks.map((callback) => (
              <div
                key={callback.id}
                className={`callback-item ${selectedCallback?.id === callback.id ? 'selected' : ''}`}
                onClick={() => setSelectedCallback(callback)}
              >
                <div className="callback-header">
                  <span
                    className="method-badge"
                    style={{ backgroundColor: getMethodColor(callback.method) }}
                  >
                    {callback.method}
                  </span>
                  <span className="callback-id">#{callback.id}</span>
                </div>
                <div className="callback-path">{callback.path}</div>
                <div className="callback-time">{formatTimestamp(callback.timestamp)}</div>
                <div className="callback-ip">{callback.ip}</div>
              </div>
            ))}

            {filteredCallbacks.length === 0 && (
              <div className="no-callbacks">
                <p>No callbacks yet</p>
                <small>Send a request to /callback to see it here</small>
              </div>
            )}
          </div>
        </div>

        <div className="detail-panel">
          {selectedCallback ? (
            <div className="callback-detail">
              <h2>Request Details</h2>

              <div className="detail-section">
                <h3>Overview</h3>
                <table>
                  <tbody>
                    <tr>
                      <td><strong>ID:</strong></td>
                      <td>{selectedCallback.id}</td>
                    </tr>
                    <tr>
                      <td><strong>Timestamp:</strong></td>
                      <td>{formatTimestamp(selectedCallback.timestamp)}</td>
                    </tr>
                    <tr>
                      <td><strong>Method:</strong></td>
                      <td>
                        <span
                          className="method-badge"
                          style={{ backgroundColor: getMethodColor(selectedCallback.method) }}
                        >
                          {selectedCallback.method}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Path:</strong></td>
                      <td>{selectedCallback.path}</td>
                    </tr>
                    <tr>
                      <td><strong>IP Address:</strong></td>
                      <td>{selectedCallback.ip}</td>
                    </tr>
                    <tr>
                      <td><strong>User Agent:</strong></td>
                      <td className="word-break">{selectedCallback.user_agent}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="detail-section">
                <h3>Query Parameters</h3>
                <pre>{JSON.stringify(parseJSON(selectedCallback.query), null, 2)}</pre>
              </div>

              <div className="detail-section">
                <h3>Headers</h3>
                <pre>{JSON.stringify(parseJSON(selectedCallback.headers), null, 2)}</pre>
              </div>

              <div className="detail-section">
                <h3>Body</h3>
                <pre>{JSON.stringify(parseJSON(selectedCallback.body), null, 2)}</pre>
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <h2>Select a callback to view details</h2>
              <p>Click on any callback from the list to see detailed information</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
